import React, { useState, useEffect } from 'react';
import { Plus, Search, Users } from 'lucide-react';
import type { Aluno, CreateAlunoDto, UpdateAlunoDto } from '../services/alunosService';
import { alunosService } from '../services/alunosService';
import { AlunoFormModal } from '../components/AlunoFormModal';
import { NovoAlunoWizard } from '../components/NovoAlunoWizard';
import turmasService from '../services/turmasService';
import { contratosService, type Contrato } from '../services/contratosService';
import { lancamentosService } from '../services/lancamentosService';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Send } from 'lucide-react';
import styles from './AlunosPage.module.css';

const FormaPagamentoLabel: Record<string, string> = {
  avista: 'À vista',
  parcelado: 'Parcelado',
  isento: 'Isento',
};

const AlunosPage: React.FC = () => {
  const [turmas, setTurmas] = useState<any[]>([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState<number | 'sem-turma' | null>(null);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [alunosSemContrato, setAlunosSemContrato] = useState<Aluno[]>([]);
  const [contratosPorTurma, setContratosPorTurma] = useState<Record<number, number>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [emitindo, setEmitindo] = useState<Set<number>>(new Set());
  const [emitindoMassa, setEmitindoMassa] = useState(false);

  // Wizard novo aluno
  const [showWizard, setShowWizard] = useState(false);

  // Modal editar aluno
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAluno, setEditingAluno] = useState<Aluno | null>(null);
  const [selectedContrato, setSelectedContrato] = useState<Contrato | null>(null);

  useEffect(() => {
    loadTurmas();
  }, []);

  useEffect(() => {
    if (selectedTurmaId !== null) {
      if (selectedTurmaId === 'sem-turma') {
        loadAlunosSemContrato();
      } else {
        loadContratos(selectedTurmaId);
      }
    }
  }, [selectedTurmaId]);

  const loadTurmas = async () => {
    try {
      const data = await turmasService.findAll();
      setTurmas(data);
      if (data.length > 0) setSelectedTurmaId(data[0].id);
      const counts = await Promise.all(
        data.map(async (turma) => {
          try {
            const lista = await contratosService.getByTurma(turma.id);
            return [turma.id, lista.length] as const;
          } catch {
            return [turma.id, 0] as const;
          }
        }),
      );
      setContratosPorTurma(Object.fromEntries(counts));
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadContratos = async (turmaId: number) => {
    setLoading(true);
    try {
      const data = await contratosService.getByTurma(turmaId);
      setContratos(data);
      setContratosPorTurma(prev => ({ ...prev, [turmaId]: data.length }));
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlunosSemContrato = async () => {
    setLoading(true);
    try {
      const data = await alunosService.getSemContrato(1);
      setAlunosSemContrato(data);
    } catch (error) {
      console.error('Erro ao carregar alunos sem contrato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmitirMassa = async () => {
    if (typeof selectedTurmaId !== 'number') return;
    setEmitindoMassa(true);
    try {
      const result = await lancamentosService.emitirParcelasTurma(selectedTurmaId);
      toast.success(`${result.emitidos} parcela(s) emitida(s) para ${result.contratos} contrato(s)`);
      loadContratos(selectedTurmaId);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao emitir parcelas em massa');
    } finally {
      setEmitindoMassa(false);
    }
  };

  const handleEmitir = async (contratoId: number) => {
    setEmitindo(prev => new Set(prev).add(contratoId));
    try {
      const result = await lancamentosService.emitirParcelas(contratoId);
      toast.success(`${result.emitidos} parcela(s) emitida(s) com sucesso`);
      if (selectedTurmaId !== null && selectedTurmaId !== 'sem-turma') {
        loadContratos(selectedTurmaId);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao emitir parcelas');
    } finally {
      setEmitindo(prev => { const s = new Set(prev); s.delete(contratoId); return s; });
    }
  };

  const handleWizardSuccess = () => {
    setShowWizard(false);
    loadTurmas();
    if (selectedTurmaId === 'sem-turma') loadAlunosSemContrato();
    else if (typeof selectedTurmaId === 'number') loadContratos(selectedTurmaId);
  };

  const handleRowClick = async (alunoId: number, contrato?: Contrato) => {
    try {
      const aluno = await alunosService.getById(alunoId);
      setEditingAluno(aluno);
      setSelectedContrato(contrato ?? null);
      setShowCreateModal(true);
    } catch (error: any) {
      toast.error('Erro ao carregar dados do aluno');
    }
  };

  const handleUpdate = async (data: CreateAlunoDto | UpdateAlunoDto) => {
    if (!editingAluno) return;
    try {
      await alunosService.update(editingAluno.id, data as UpdateAlunoDto);
      toast.success('Aluno atualizado com sucesso');
      setShowCreateModal(false);
      setEditingAluno(null);
      if (selectedTurmaId === 'sem-turma') loadAlunosSemContrato();
      else if (selectedTurmaId !== null) loadContratos(selectedTurmaId);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar aluno');
    }
  };

  const filteredContratos = contratos.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.aluno?.nome.toLowerCase().includes(term) ||
      c.responsavelPrincipal?.nome.toLowerCase().includes(term) ||
      String(c.numeroSequencial).includes(term)
    );
  });

  const filteredAlunosSemContrato = alunosSemContrato.filter(a => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return a.nome.toLowerCase().includes(term) || a.matricula.toLowerCase().includes(term);
  });

  const valorFinalMensal = (c: Contrato) =>
    Number(c.valorMensal) * (1 - Number(c.percentualDescontoMensal) / 100);

  return (
    <Layout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Users size={32} />
              Alunos
            </h1>
            <p className={styles.subtitle}>Contratos por turma</p>
          </div>
          <div className={styles.headerActions}>
            {typeof selectedTurmaId === 'number' && contratos.some(c => !c.parcelasMatriculaEmitidas || !c.parcelasMensalidadesEmitidas) && (
              <button
                className={styles.emitirMassaButton}
                onClick={handleEmitirMassa}
                disabled={emitindoMassa}
              >
                {emitindoMassa
                  ? <><span className={styles.emitindoSpinnerDark} /> Emitindo...</>
                  : <><Send size={16} /> Emitir em Massa ({contratos.filter(c => !c.parcelasMatriculaEmitidas || !c.parcelasMensalidadesEmitidas).length})</>}
              </button>
            )}
            <button className={styles.createButton} onClick={() => setShowWizard(true)}>
              <Plus size={20} />
              Novo Aluno
            </button>
          </div>
        </div>

        {/* Abas de Turmas */}
        <div className={styles.turmasTabsContainer}>
          {turmas.map((turma) => (
            <button
              key={turma.id}
              className={`${styles.turmaTab} ${selectedTurmaId === turma.id ? styles.turmaTabActive : ''}`}
              onClick={() => setSelectedTurmaId(turma.id)}
            >
              <span>{turma.nome}</span>
              <small>{typeof contratosPorTurma[turma.id] === 'number'
                ? `${contratosPorTurma[turma.id]} contrato${contratosPorTurma[turma.id] === 1 ? '' : 's'}`
                : '—'}</small>
            </button>
          ))}
          <button
            className={`${styles.turmaTab} ${selectedTurmaId === 'sem-turma' ? styles.turmaTabActive : ''}`}
            onClick={() => setSelectedTurmaId('sem-turma')}
          >
            <span>Sem Turma</span>
            <small>{alunosSemContrato.length} aluno{alunosSemContrato.length === 1 ? '' : 's'}</small>
          </button>
        </div>

        {/* Busca */}
        <div className={styles.filtersContainer}>
          <div className={styles.searchBox}>
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por aluno, responsável ou Nº contrato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Tabela */}
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <p>Carregando...</p>
          </div>
        ) : selectedTurmaId === 'sem-turma' ? (
          filteredAlunosSemContrato.length === 0 ? (
            <div className={styles.emptyState}>
              <Users size={48} />
              <h3>Nenhum aluno sem contrato</h3>
              <p>Todos os alunos possuem contrato em 2026</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Matrícula</th>
                    <th className={styles.thNome}>Aluno</th>
                    <th>CPF</th>
                    <th>Telefone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlunosSemContrato.map((a) => (
                    <tr key={a.id} onClick={() => handleRowClick(a.id)} className={styles.clickableRow}>
                      <td className={styles.tdNumero}>{a.matricula}</td>
                      <td className={styles.thNome}><strong>{a.nome}</strong></td>
                      <td>{a.cpf ?? '—'}</td>
                      <td>{a.telefone ?? '—'}</td>
                      <td>
                        <span className={a.ativo ? styles.badgePago : styles.badgePendente}>
                          {a.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : filteredContratos.length === 0 ? (
          <div className={styles.emptyState}>
            <Users size={48} />
            <h3>Nenhum contrato encontrado</h3>
            <p>Nenhum aluno matriculado nesta turma</p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th className={styles.thNome}>Aluno</th>
                  <th>Responsável</th>
                  <th>Mensalidade</th>
                  <th>Desconto</th>
                  <th>Valor Final</th>
                  <th>Matrícula</th>
                  <th>Pagto. Matrícula</th>
                  <th>Matr. Paga</th>
                  <th title="Parcela de matrícula emitida" style={{ textAlign: 'center' }}>Matr. Emit.</th>
                  <th title="Parcelas de mensalidades emitidas" style={{ textAlign: 'center' }}>Mens. Emit.</th>
                  <th style={{ textAlign: 'center' }}>Emitir</th>
                </tr>
              </thead>
              <tbody>
                {filteredContratos.map((c) => (
                  <tr key={c.id} onClick={() => c.aluno?.id && handleRowClick(c.aluno.id, c)} className={styles.clickableRow}>
                    <td className={styles.tdNumero}>{c.numeroSequencial}</td>
                    <td className={styles.thNome}>
                      <strong>{c.aluno?.nome ?? <em style={{ color: '#999' }}>Sem vínculo</em>}</strong>
                      {c.eRematricula && (
                        <span className={styles.badgeRematricula}>Rematrícula</span>
                      )}
                    </td>
                    <td>{c.responsavelPrincipal?.nome ?? '—'}</td>
                    <td className={styles.tdValor}>
                      R$ {Number(c.valorMensal).toFixed(2).replace('.', ',')}
                    </td>
                    <td className={styles.tdDesconto}>
                      {Number(c.percentualDescontoMensal) > 0
                        ? `${Number(c.percentualDescontoMensal).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td className={styles.tdValorFinal}>
                      R$ {valorFinalMensal(c).toFixed(2).replace('.', ',')}
                    </td>
                    <td className={styles.tdValor}>
                      R$ {Number(c.valorMatricula).toFixed(2).replace('.', ',')}
                    </td>
                    <td>{FormaPagamentoLabel[c.formaPagamentoMatricula]}</td>
                    <td>
                      <span className={c.matriculaPaga ? styles.badgePago : styles.badgePendente}>
                        {c.matriculaPaga ? 'Paga' : 'Pendente'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {c.parcelasMatriculaEmitidas
                        ? <CheckCircle size={18} color="#16a34a" />
                        : <XCircle size={18} color="#dc2626" />}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {c.parcelasMensalidadesEmitidas
                        ? <CheckCircle size={18} color="#16a34a" />
                        : <XCircle size={18} color="#dc2626" />}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(!c.parcelasMatriculaEmitidas || !c.parcelasMensalidadesEmitidas) && (
                        <button
                          className={styles.emitirBtn}
                          onClick={(e) => { e.stopPropagation(); handleEmitir(c.id); }}
                          disabled={emitindo.has(c.id)}
                          title="Emitir parcelas deste contrato"
                        >
                          {emitindo.has(c.id)
                            ? <span className={styles.emitindoSpinner} />
                            : <><Send size={14} /> Emitir</>}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <NovoAlunoWizard
          isOpen={showWizard}
          anoLetivoId={1}
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />

        <AlunoFormModal
          isOpen={showCreateModal}
          aluno={editingAluno}
          contrato={selectedContrato}
          onClose={() => { setShowCreateModal(false); setEditingAluno(null); setSelectedContrato(null); }}
          onSubmit={handleUpdate}
        />
      </div>
    </Layout>
  );
};

export default AlunosPage;
